/**
 * PaneSection — a collapsible section of the room side pane (Step 11c, SPEC section 8).
 *
 * The header is a button with a chevron and `aria-expanded`. The open state is
 * remembered per section in localStorage (a per-viewer convenience: every access
 * is wrapped, and the default applies whenever storage is unavailable).
 */

import { ReactNode, useId, useState } from 'react'
import { ChevronDown, ChevronRight } from '@digital-twins/design-system/icons'

import { Text } from '@/components/ui/Text'
import { readStoredOpen, writeStoredOpen } from '@/lib/storage/storedOpen'

const STORAGE_PREFIX = 'room-pane-section:'

interface PaneSectionProps {
  /** Stable section id, used as the storage key. */
  sectionId: string
  title: string
  count?: number
  isDefaultOpen?: boolean
  /** Section test id; the header button gets `${dataTestId}-toggle`. */
  dataTestId: string
  children: ReactNode
}

export function PaneSection(props: PaneSectionProps) {
  const { sectionId, title, count, isDefaultOpen = true, dataTestId, children } = props

  const [isOpen, setIsOpen] = useState(() => readStoredOpen(`${STORAGE_PREFIX}${sectionId}`) ?? isDefaultOpen)
  const contentId = useId()

  const onToggle = () => {
    const next = !isOpen
    setIsOpen(next)
    writeStoredOpen(`${STORAGE_PREFIX}${sectionId}`, next)
  }

  const Chevron = isOpen ? ChevronDown : ChevronRight

  return (
    <section className="flex flex-col gap-2" data-testid={dataTestId}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={contentId}
        data-testid={`${dataTestId}-toggle`}
        className="flex w-full items-center justify-between gap-2 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-accent"
      >
        <Text size="sm" className="font-bold leading-[1.4] text-twin-chat-fg">
          {title}
          {count !== undefined && (
            <span className="ml-1.5 font-normal text-twin-chat-muted">{count}</span>
          )}
        </Text>
        <Chevron className="h-4 w-4 shrink-0 text-twin-chat-muted" aria-hidden="true" />
      </button>
      {isOpen && <div id={contentId}>{children}</div>}
    </section>
  )
}
